import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { faChevronLeft, faChevronRight, faDownload, faFile, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { PageService, ResizeService, SortService } from '@syncfusion/ej2-angular-grids';
import { base64StringToBlob } from 'blob-util';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { UserUploadFile } from '../../../models/userUploadFile';
import { FileUploadService } from '../../../services/FileUpload.service';
import { FarmFileKind, getFileKind } from '../farm-files/farm-file-utils';

interface SheetPreview {
  name: string;
  columns: any[];
  rows: any[];
  truncated: boolean;
}

const MAX_PREVIEW_ROWS = 5000;

@Component({
  selector: 'kw-farm-file-viewer-dialog',
  templateUrl: './farm-file-viewer-dialog.component.html',
  styleUrls: ['./farm-file-viewer-dialog.component.scss'],
  // Dialogs are created outside the lazy module's injector, so the grid features are provided here.
  providers: [PageService, SortService, ResizeService]
})
export class FarmFileViewerDialogComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  public files: UserUploadFile[];
  public index: number;
  public isLoading = false;
  public loadFailed = false;

  public kind: FarmFileKind = 'other';
  public imageSrc: string;
  public pdfUrl: SafeResourceUrl;
  public sheets: SheetPreview[] = [];

  public faClose = faTimes;
  public faDownload = faDownload;
  public faPrev = faChevronLeft;
  public faNext = faChevronRight;
  public faFile = faFile;

  public rawBase64: string;
  private blobUrl: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { files: UserUploadFile[], index: number },
    private dialogRef: MatDialogRef<FarmFileViewerDialogComponent>,
    private fileService: FileUploadService,
    private errorService: ErrorService,
    private sanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
  ) {
    this.files = data.files;
    this.index = Math.max(0, data.index);
  }

  get current(): UserUploadFile {
    return this.files[this.index];
  }

  ngOnInit() {
    this.loadCurrent();
  }

  ngOnDestroy() {
    this.releaseBlobUrl();
  }

  close() {
    this.dialogRef.close();
  }

  step(delta: number) {
    const next = this.index + delta;
    if (next < 0 || next >= this.files.length) return;
    this.index = next;
    this.loadCurrent();
  }

  download() {
    if (!this.rawBase64) return;
    FileSaver.saveAs(base64StringToBlob(this.rawBase64), this.current.fileName);
  }

  private releaseBlobUrl() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  private loadCurrent() {
    const file = this.current;
    this.releaseBlobUrl();
    this.imageSrc = null;
    this.pdfUrl = null;
    this.sheets = [];
    this.rawBase64 = null;
    this.loadFailed = false;
    this.kind = getFileKind(file.fileName);
    this.isLoading = true;

    // Bytes are fetched per file on demand; the list only carries metadata/thumbnails.
    this.fileService.getFileById(file.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (full) => {
        if (file !== this.current) return; // user already navigated away
        this.rawBase64 = full.fileByte;
        this.render();
        this.isLoading = false;
        this.changeDetector.markForCheck();
      },
      error: (err) => {
        this.isLoading = false;
        this.loadFailed = true;
        this.errorService.handleError(err);
      }
    });
  }

  private render() {
    if (!this.rawBase64) {
      this.loadFailed = true;
      return;
    }
    switch (this.kind) {
      case 'image':
        this.imageSrc = `data:image/jpeg;base64,${this.rawBase64}`;
        break;
      case 'pdf':
        this.blobUrl = URL.createObjectURL(base64StringToBlob(this.rawBase64, 'application/pdf'));
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
        break;
      case 'excel':
        try {
          this.sheets = this.parseWorkbook(this.rawBase64);
        } catch {
          // Unreadable workbook: fall back to the download-only view.
          this.kind = 'other';
        }
        break;
    }
  }

  private parseWorkbook(base64: string): SheetPreview[] {
    const workbook = XLSX.read(base64, { type: 'base64' });
    return workbook.SheetNames.map(name => {
      const all: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: '' });
      const truncated = all.length - 1 > MAX_PREVIEW_ROWS;
      const header = all[0] || [];
      const body = all.slice(1, MAX_PREVIEW_ROWS + 1);
      const width = Math.max(header.length, ...body.map(r => r.length), 0);

      // Clean data objects keyed by a unique, stable field name per column.
      const columns = Array.from({ length: width }, (_, i) => ({
        field: `c${i}`,
        headerText: String(header[i] ?? '') || XLSX.utils.encode_col(i),
        width: 140,
      }));
      const rows = body.map(r => {
        const row: any = {};
        columns.forEach((c, i) => row[c.field] = r[i] ?? '');
        return row;
      });
      return { name, columns, rows, truncated };
    });
  }
}
